// The landing page's words, in one place (founder pick A, "Set yourself
// apart", 2026-09-29, ADR 0022). Every heading, intro and line the layout
// shows is read from here (the gallery's and /tour's from landingTour.js,
// the evidence figures from data/landingEvidence.js), so a new message can be
// swapped in without touching a component. The copy is the mock round's
// (.playwright-mcp/landing-mocks/copy.md, "Shared blocks" + "Variant A"),
// word for word, with the page order the founder amended the same day.
//
// The four tools read their names from content/buildNames.js; the guide
// links read their slugs from the paper modules themselves, so a renamed
// paper moves the link with it (the mock typed two slugs that no longer
// existed; landingHome.test.js now resolves every link against the
// registry). Counts come from constants/catalog.js, never typed here. The
// `*Cta` fields are ids in content/landingIds.js (the landing watch, #2512),
// written out here so its drift test can read every one.

import { BUILD_NAMES } from './buildNames';
import { STORY_TAGLINE } from './brandLine';
import {
    UNIVERSITY_COUNT, MAJORS_LABEL, SUMMER_PROGRAM_COUNT,
    GATED_MAJORS, GATED_MAJOR_SCHOOLS,
} from '../constants/catalog';
import theBuilderTrack from '../data/resources/the-builder-track';
import patentPending from '../data/resources/patent-pending-on-your-application';
import ownResearch from '../data/resources/your-own-research-on-your-application';
import readingYourOdds from '../data/resources/reading-your-odds';
import safetySchool from '../data/resources/what-is-a-safety-school';
import majorTrap from '../data/resources/boilerplate-major-trap';
import roadmapPaper from '../data/resources/how-stratia-builds-roadmap';
import essayPaper from '../data/resources/the-essay-only-you-can-write';
import realDecisions from '../data/resources/reading-real-decisions';
import withCounselorPaper from '../data/resources/stratia-with-your-counselor';
import sharingPaper from '../data/resources/share-your-application';
import costPaper from '../data/resources/what-college-actually-costs';
import visitsPaper from '../data/resources/why-campus-visits-matter';

const guide = (paper) => `/resources/${paper.slug}`;

// One label for every sign-up button on the page, the header's included.
export { START_FREE } from './ctaLabels';

// The message is founder pick E (2026-09-29, "E, with the patent tile
// fixed"; ADR 0022): build first, the profile follows.
export const PAGE_TITLE = 'Stratia Admissions - Build Something Real. The Profile Follows.';
export const PAGE_DESCRIPTION = 'Your student builds something real: a patent filing, a research paper, a pitch. Stratia plans from what they made: the odds, the list, the roadmap, wherever they are headed. Start free.';

// The free read box's heading and the line under it (founder pick "1",
// 2026-09-29: the box stays where it is, option A). Only these two lines are
// the landing page's; everything inside the box stays word for word, and the
// box keeps its #see-your-odds anchor (the ads' landing target).
// The pick's last sentence, "What you build from here changes the answer.",
// is replaced: the free read scores grades, scores and major fit only
// (preview_fit.py: holistic_conf 'not_judged'), and the box's own card tells
// the visitor activities are NOT judged there. The full fit analysis does read
// them (fit_computation.py), which is what the line now says (code review,
// #2518; ADR 0022).
export const FREE_READ = {
    heading: 'Headed to college? See where you stand today.',
    sub: "Type your grades and activities like you'd text a friend, then ask about any school. The read weighs your grades and scores; what you build counts in the full analysis.",
};

export const HERO = {
    title: 'Build something real. The profile follows.',
    sub: 'Your student finds an idea of their own and makes it real: a filing, a paper, a pitch. Stratia plans from what they made, wherever they are headed.',
};

// The founder's idea in one line, right under the hero's words.
export const CONTRAST = 'Build real things and let them shape your profile, instead of putting a profile together to get into college.';

// The Build set grows (founder: "our builder portfolio will only expand with
// more tools"). No names, no dates.
export const MORE_TOOLS = {
    title: 'More on the way',
    body: 'These are the first tools. New ones join this list when they are ready.',
};

// Three steps. "In one step", never "automatically": a recorded filing
// (builder.add_filing_to_application) and a submitted paper
// (study.add_to_application) each add one plainly worded activity when the
// student asks; the pitch writes nothing to the profile, so no step says
// every tool does.
export const HOW = {
    eyebrow: 'Build first',
    title: 'How it works',
    steps: [
        { title: 'Build', body: `Start with ${BUILD_NAMES.home.label}: seven questions about a problem you have noticed. Then take the idea to a filing, a paper or a pitch.` },
        { title: 'It goes on the record', body: 'A filed application or a submitted paper goes onto your activities in one step, worded plainly, with its date. Nothing gets dressed up.' },
        { title: 'Point it where you are headed', body: `Every read Stratia does starts from that profile: your odds at ${UNIVERSITY_COUNT} universities, your list and roadmap, summer programs and scholarships.` },
    ],
};

// The trends band: figures from data/landingTrends.js, by key, in order.
export const TRENDS = {
    eyebrow: 'What the sources say',
    title: 'Students already build. It still stands out.',
    keys: ['appchallenge', 'isef', 'sts', 'harvard'],
    honest: 'None of this gets anyone admitted or hired. It shows real work is common enough to try and rare enough to notice.',
};

// Off the landing page (founder, 2026-09-30, #2558): "this should not be in
// the landing page... we should start another section in the top as blog or
// news". Kept here, word for word, as the first item for that section.
// One pointer, low on the page: think past the usual path. Not a trend,
// never first, and not an endorsement or a partnership (it says so).
export const POINTER = {
    label: 'Think past the usual path',
    text: 'One new school is built around doing real work. On September 22, 2026, a16z announced the Horowitz Andreessen Academy, for young people coming out of high school, built on co-ops, courses and "pursuits": personal projects, quests and startup ideas.',
    link: 'Read the announcement',
    url: 'https://a16z.com/announcement/incubating-horowitz-andreessen-academy/',
    source: 'a16z.com, Sep 22, 2026. Stratia has no connection to it.',
};

// The sections under the hero, in order (a key per block LandingPageV2 knows).
// 'colleges' (#4434 plan A, founder pick A1, 2026-10-16): Look up a college,
// right after How it works.
// 'with_counselor' (#4972, founder 2026-10-20): right after the planning
// section that names the tools, and before the gallery. Far below the free
// read, so the #see-your-odds anchor and the page's first action never move.
export const ORDER = ['free_read', 'how', 'colleges', 'trends', 'build', 'planning', 'with_counselor', 'gallery'];
// The Master's abroad section (#4967), India visitors only, sits right above
// the "Build something real" hero and its tools (founder, 2026-10-20: "the
// build something real and the tools should go below the Planning a master's
// section"): under Ask Astra and the proof strip, which keep the ad landing
// anchor and the page's first action.
export const MASTERS_BEFORE = 'hero';

const NUMBER_WORD = { 1: 'one', 2: 'two', 3: 'three', 4: 'four' };
/** "four" for four tools; the page counts only the tools it shows. */
export const toolCountWord = (n) => NUMBER_WORD[n] || String(n);

// The four tools, in the listing order Build uses.
export const TOOLS = [
    {
        key: 'brainstorm',
        name: BUILD_NAMES.home.label,
        short: 'Find an idea in your own record. Seven questions, your words.',
        line: 'Seven questions about a problem you have noticed, answered in your own words. Astra helps you think. She never writes your answers.',
        get: 'A write-up where every sentence shows which answer it came from.',
        guide: guide(theBuilderTrack),
        tileCta: 'build_tile_brainstorm',
        guideCta: 'build_guide_brainstorm',
    },
    {
        key: 'patent',
        name: BUILD_NAMES.steps.patent.label,
        short: 'Turn it into a provisional patent application you file yourselves.',
        line: 'Search what already exists, turn your idea into a provisional patent application with figures, and file it yourselves with the US Patent Office.',
        get: 'An application number and a filing date.',
        guide: guide(patentPending),
        tileCta: 'build_tile_patent',
        guideCta: 'build_guide_patent',
        attorneyAside: true,
    },
    {
        key: 'research',
        name: BUILD_NAMES.steps.research.label,
        short: 'Write a research paper, reviewed by questions, never rewritten.',
        line: 'Ask a question that is yours and write a research paper. A mentor reviews it by asking questions. It never rewrites your work.',
        get: 'The paper, plus a log of every question the mentor asked. Journals and science fairs ask for that.',
        guide: guide(ownResearch),
        tileCta: 'build_tile_research',
        guideCta: 'build_guide_research',
    },
    {
        key: 'pitch',
        name: BUILD_NAMES.steps.pitch.label,
        short: 'Build a short deck and practice it out loud.',
        line: 'Turn the same idea into a short deck with a source on every number. Practice it out loud and get three fixes after every run.',
        get: 'A deck you can present, and the numbers from every practice run.',
        guide: null, // no guide for this tool yet: its card's action is Start free
        tileCta: 'build_tile_pitch',
        startCta: 'build_start_free_pitch',
    },
];

const Word = (n) => toolCountWord(n).charAt(0).toUpperCase() + toolCountWord(n).slice(1);

export const BUILD_SECTION = {
    eyebrow: 'Build',
    title: (n) => `${Word(n)} tools today. One idea that is yours.`,
    sub: `Start with ${BUILD_NAMES.home.label}. Then take the idea wherever it fits: a filing, a paper, a pitch, or all three.`,
    strip: (n) => `Today's ${toolCountWord(n)} tools, and more on the way`,
    heroLink: (n) => `See the ${toolCountWord(n)} tools`,
};

export const PLANNING = {
    eyebrow: 'Planning',
    title: 'And we plan the rest.',
    sub: "The list, the odds, the deadlines and the essays, with Astra doing the work alongside them.",
    items: [
        { title: `Your odds at ${UNIVERSITY_COUNT} universities`, body: "Read against each school's own published data, major by major. Free.", to: guide(readingYourOdds) },
        { title: 'A list that is balanced, not hopeful', body: 'Reach, target and safety, with the reasons on every card.', to: guide(safetySchool) },
        { title: 'Majors before schools', body: 'The majors your record supports, so you do not default to the most crowded one.', to: guide(majorTrap) },
        { title: 'A roadmap to graduation', body: 'Every deadline on one line, paced to your year.', to: guide(roadmapPaper) },
        { title: 'Essays in your own voice', body: 'Guided questions and honest feedback. The coach never writes sentences for you.', to: guide(essayPaper) },
        { title: 'What happened to students like you', body: 'Decisions past applicants reported, compared with your own record.', to: guide(realDecisions) },
    ],
    // Astra is never called a counselor (#4804, founder 2026-10-19 "proceed";
    // counselors are partners). Replaced the 2026-09-30 pick "A college
    // counselor who never logs off." True as written: Astra answers at any
    // hour. Never "unlimited": plans cap how much she does.
    astraHeading: 'Astra, for questions any time.',
    astra: "Astra reads your student's whole record before every answer.",
    astraRest: 'She does the work in the app, and she asks before she changes anything.',
};

// The data behind the plan (#2527): the top of the planning section. Every
// figure is a catalog.js constant, re-counted the night it shipped
// (2026-09-29). From live Firestore: 248 universities; 24,110 decision
// records across 177 schools; 7,502 distinct majors; gated majors are
// derived from majorDoors.json by generate_major_doors.py (1,475 across 84
// schools on 2026-10-11, #3818). From counselor_agent/summer_programs.json: 261
// programs, the 257 named ones each verified against its official site plus
// four generic options, hence "every named program" below. School Reads is
// left out: usageStats.js has not been recounted since Sep 9, and a number
// without a same-day recount does not ship.
const grouped = (n) => n.toLocaleString('en-US');

export const PLANNING_DATA = {
    label: 'The data behind the plan',
    tiles: [
        { key: 'universities', figure: grouped(UNIVERSITY_COUNT), label: 'universities', why: "Each read uses that school's own published numbers, not a national average." },
        { key: 'majors', figure: MAJORS_LABEL, label: 'majors cataloged', why: 'The plan starts from what your student wants to study, school by school.' },
        { key: 'programs', figure: grouped(SUMMER_PROGRAM_COUNT), label: 'summer programs', why: 'Every named program is checked against its own official site.' },
        { key: 'gated', figure: grouped(GATED_MAJORS), label: `gated majors flagged across ${GATED_MAJOR_SCHOOLS} schools`, why: 'Where getting into the school is not getting into the major, you know before you apply.' },
    ],
};

// The proof strip under Ask Astra (#3820, mock variant A) and the milestone
// ribbon (variant C). The figures and their labels come from the server
// (get-landing-stats; the rules in workflow_jobs/landing_stats.py): nothing
// shows until the founder's Go live on the Desk, and a figure shows only once
// it clears its threshold. No cadence wording here (standing rule).
export const PROOF_STRIP = {
    label: 'Stratia by the numbers',
    footnote: 'Counted from our own data, updated as it grows.',
};
// Where people come from (#4607, mock D; #4742). Founder, 2026-10-18 ~3:35
// PM PT: "if 1 person uses, it should show the state in the map", and "this
// should include people who view shorts as well". A place is shaded once one
// real person there has visited the site, signed in, or been shown one of our
// videos on YouTube or Instagram (workflow_jobs/landing_stats.py). Every
// number in the line comes from get-landing-stats' reach block, never from
// this file, and the page never shows a count per place. Density (#4768):
// a reached place is shaded by its step of PEOPLE (visitors and signed-in
// students); a place reached only by our videos takes the lightest shade.
export const VISITOR_MAPS = {
    heading: 'Exploring colleges with Stratia',
    statesLabel: 'states',
    countriesLabel: 'countries',
    line: ({ states, countries }) => (countries
        ? `People in ${states} states and ${countries} countries have used Stratia or seen our videos`
        : `People in ${states} states have used Stratia or seen our videos`),
    finePrint: 'Color shows how many people in each place visited Stratia or signed in, stripes mark places that have only seen our videos, and the map updates hourly.',
    tabs: { us: 'United States', world: 'World' },
    // A phone opens the maps on a tap, so the band stays short above the
    // hero (layout budget, landing-layout.spec.js); a desktop shows them.
    toggle: { open: 'See the map', close: 'Hide the map' },
    mapLabel: {
        us: ({ states }) => `Map of the United States with ${states} states shaded`,
        world: ({ countries }) => `World map with ${countries} countries shaded`,
    },
    legend: { fewer: 'Fewer', more: 'More', videosOnly: 'Seen our videos only', notYet: 'Not yet' },
    // A hover or tap on a place: its name and one of these, never a number.
    place: { reached: 'reached', notYet: 'not yet' },
};
export const MILESTONE_RIBBON = {
    dismiss: 'Dismiss',
    badgeLabel: 'Milestone',
};

export const PRIVACY_LINE = {
    text: "We take your student's name, email and phone number out before Astra analyzes anything, and never sell their data. We record how visitors use this site, with what they type masked, so we can improve it.",
    link: 'Our Student Data Principles',
    to: '/student-data-principles',
};

// Stratia alongside a counselor (#4972). Founder, 2026-10-20 (US spelling
// here, usSpellingGuard): "Stratia
// advantage even when working with a counselor... connects the dots based on
// the student profile interests to find the right fit colleges, best
// activities, to summer programs and essay ideas", "going with a solid plan
// and ground work the time spent with the counselor would provide the
// maximum ROI", "Sharing insights back and forth using the sharing feature".
//
// Counselors are partners (founder 2026-10-19): the counselor brings the
// judgment, Stratia the groundwork, never a knock on any counselor, and Astra
// is never called one. Every feature named is live for a signed-in student
// today; `route` is the page it lives on, and WithCounselor.test.jsx checks
// each against App.jsx (data only, never rendered: the pages need sign-in).
// Sharing is what share_gateway does today, and no more: a private link to
// the sections the student picks (config/shareScopes.js), no account on the
// reviewer's side, read live on every open, and, on a link the student opened
// to comments (#4987, live since its launch), a note back under the item it
// is about that the student answers inside Stratia.
// `masters` shows only beside the Master's section (india_masters + an India
// visitor, mastersSectionVisible) and stays in the future tense: no master's
// feature is live.
export const WITH_COUNSELOR = {
    eyebrow: 'With your counselor',
    title: 'Working with a counselor? Make the time together go further.',
    sub: 'Stratia connects the dots from your own record and interests to the colleges that fit, the activities worth building, the programs worth applying to and the essay ideas worth writing. Bring that groundwork to each session, and the hour goes to strategy instead of catching up.',
    items: [
        { key: 'fit', title: 'Colleges that fit', body: 'Each school read against your record, with the reasons behind every reach, target and safety.', route: '/launchpad' },
        { key: 'activities', title: 'Activities worth building', body: 'Ideas matched to your record and the majors you are drawn to, each with the why and a first step.', route: '/activities' },
        { key: 'programs', title: 'Programs worth applying to', body: 'Summer and school-year programs picked for your record, every named one checked against its own official site.', route: '/programs' },
        { key: 'essays', title: 'Essay ideas from your own story', body: 'Guided questions and starters, then an outline and feedback on your drafts. The words stay yours.', route: '/essay-help/:universityId' },
    ],
    shareTitle: 'Share it, and keep the plan in step',
    share: 'Send your counselor a private link to the parts you choose: your college list with its fit reads, your essays, your roadmap. They need no account, the link shows your latest work each time they open it, and they can leave a note right on the school or essay it is about. Answer it in Stratia, and the strategy you build together leaves nothing out.',
    shareRoute: '/share',
    shareLink: 'How sharing works',
    shareGuide: guide(sharingPaper),
    partner: 'Your counselor brings the judgment and the experience. Stratia brings the groundwork.',
    guideLink: withCounselorPaper.title,
    guide: guide(withCounselorPaper),
    masters: "Applying for a master's? Stratia will bring the same groundwork to master's applications when its program pages open.",
};

export const COUNSELOR_LINE = {
    lead: 'School counselor?',
    link: 'See what Stratia does for your caseload',
    to: '/for-schools',
};

// ============================================================================
// THE STORY ORDER (#5128): founder pick A of the landing redesign mocks
// (#5093, https://claude.ai/artifact/G4u9UhG8s1phYNeQNh4x3V, picked on the
// Desk 2026-10-22), behind the landing_story flag. Off, everything above
// renders as it does today. The copy is design.md section 5, word for word,
// each line run through scripts/check_copy.py. The founder's answers to the
// kit's questions, which are decisions here:
//   1. the Season Pass price shows on the page (from the price constant);
//   2. "high school" until the app takes an 8th grader: no grade is named;
//   3. Through Graduation joins the cost band the day one_payment_passes is
//      on for everyone;
//   4. the six product screens fold to one Take the tour line;
//   5. the title and the Google description change with the headline;
//   6. the trends band keeps one sourced line, in the Build band;
//   7. read as 28 days before vs 28 days after (the Desk's landing watch);
//   8. the quotes get their own band, right under the reach map.
// ============================================================================

export const STORY_PAGE_TITLE = 'Stratia Admissions - Start With Your Story. See Where It Fits.';
export const STORY_PAGE_DESCRIPTION = "The college plan that starts from your own story. Stratia reads your grades, activities and awards against each college's own published data, then lays out the list, the roadmap, the essays and the cost, with the source on every answer. Start free.";
// The footer's line under the brand changes with the headline (product.md
// section 11). Since R-29 (2026-10-23) it is the brand line on every public
// page, defined once in brandLine.js.
export { STORY_TAGLINE };

// Over Astra's chat (the page's one h1), or over the free read when the chat
// is closed.
export const STORY_HERO = {
    heading: STORY_TAGLINE,
    sub: "Tell Stratia your grades, activities, awards and projects in your own words. It reads every college on your list from that school's own published data, and every answer shows its source.",
};

// The bands under the reach band, in order (a key per block LandingPageV2
// knows). 'colleges' and 'with_counselor' keep their ids: the same content,
// moved.
export const STORY_ORDER = ['quotes', 'story', 'colleges', 'plan', 'build', 'with_counselor', 'cost', 'tour_line'];

export const STORY_QUOTES = {
    eyebrow: 'What students and parents say',
    startCta: 'quotes_start_free',
};

// Numbered: they are a sequence. Step 3's "asks before she changes anything"
// is the concierge's confirm card (product invariant 5).
export const STORY_STEPS = {
    eyebrow: 'How Stratia works',
    title: 'One story. One plan.',
    steps: [
        { title: 'Tell your story', body: "Type it the way you'd text a friend, or drop in a transcript or a resume: grades, activities, awards, certificates, projects. Stratia builds your profile from it, worded plainly." },
        { title: 'See what each college weighs', body: `Your odds at ${UNIVERSITY_COUNT} colleges, read against each school's own published numbers, major by major. Reach, target and safety, with the reasons on every card.` },
        { title: 'Follow the plan', body: 'A roadmap to graduation, essays in your own voice, what families paid after aid, visits and events. Astra does the work alongside you and asks before she changes anything.' },
    ],
    startCta: 'story_start_free',
};

// Four parts of "And we plan the rest" (the list, majors, the roadmap, the
// essays), plus cost and visits. The data band (PLANNING_DATA) sits on top.
export const STORY_PLAN = {
    eyebrow: 'Your plan',
    title: 'Everything the plan covers.',
    sub: 'Each part links to a free guide on how it works.',
    items: [
        PLANNING.items[1],
        PLANNING.items[2],
        PLANNING.items[3],
        PLANNING.items[4],
        { title: 'What it will really cost', body: 'What aided families paid at each school, by income, and for seniors the FAFSA and CSS Profile dates from its own page.', to: guide(costPaper) },
        { title: 'Visits and events', body: 'Campus visits and the events of the schools on your list, in one place.', to: guide(visitsPaper) },
    ],
    guideCta: 'plan_guide',
};

// The four tools as one band, lower down: a way to add something real to the
// story, not the page's lead. The tiles keep their build_tile_* ids; a tool
// with no guide of its own (the pitch) links the builder-track guide. One
// sourced trend line (question 6): data/landingTrends.js TREND.appchallenge.
export const STORY_BUILD = {
    eyebrow: 'Build',
    title: 'Want more to show? Build something real.',
    sub: 'When your story needs something new, the builder tools help you make it: an idea of your own, then a filing, a paper or a pitch. What you file or submit goes onto your record in one step.',
    trend: 'appchallenge',
    guide: guide(theBuilderTrack),
    guideLink: theBuilderTrack.title,
    guideCta: 'build_guide',
};

// With your counselor, in one card. Its partner line and links are
// WITH_COUNSELOR's, so the two cannot drift.
export const STORY_COUNSELOR = {
    eyebrow: WITH_COUNSELOR.eyebrow,
    title: 'Working with a counselor? Bring the groundwork.',
    body: 'Stratia does the groundwork from your own record: the colleges that fit, the activities worth building, the programs and essay ideas. Share a private link to the parts you choose, and your counselor can leave a note right on the school or essay it is about.',
    partner: WITH_COUNSELOR.partner,
    shareLink: WITH_COUNSELOR.shareLink,
    shareGuide: WITH_COUNSELOR.shareGuide,
    guideLink: WITH_COUNSELOR.guideLink,
    guide: WITH_COUNSELOR.guide,
    masters: WITH_COUNSELOR.masters,
};

// What it costs (question 1). Prices are never typed here: the band reads the
// visitor's price from useSeasonPassPrice (constants/seasonPricing.js, the
// rupee pass for an India visitor while india_masters is on) and hands the
// label in. No credit, usage or meter word (product invariant 1), never
// "unlimited". "a year" holds whether or not the Pass renews
// (one_payment_passes), and the band says nothing about renewal.
export const STORY_COST = {
    eyebrow: 'What it costs',
    title: "Start free. Everything for one season when you're ready.",
    free: { title: 'Free', body: 'Your odds at every school, your list, and Astra for questions.' },
    trial: { title: '7 days of everything', body: 'From your first list read. No card, nothing to cancel.' },
    // In the trial's place while one_free_read is on for everyone (#5909): a
    // new account gets its first full read free and no trial (#5631).
    firstRead: { title: 'Your first full read, free', body: 'One college in full: your fit, major chances and the plan. No card.' },
    pass: { title: (price) => `Season Pass, ${price} a year`, body: 'Everything in Stratia, all season.' },
    // Question 3: shown only once one_payment_passes is on for everyone.
    graduation: { title: (price) => `Through Graduation, ${price} once`, body: 'For 9th to 12th graders: every season until your applications are done.' },
    pricing: 'See pricing',
    startCta: 'cost_start_free',
    pricingCta: 'cost_pricing',
};

// The six product screens fold to one line (question 4); /tour has them all.
export const STORY_TOUR = {
    lead: 'Want to see the screens first?',
    link: 'Take the tour',
    to: '/tour',
};
